// Iter-f5 — Brand Cancellation Dashboard (summary / branch cards),
// dual-period edition.
//
// The single date range filter has been retired in favour of two fully
// custom range pickers (Period A vs Period B) that mirror the layout
// and behaviour of the Service Performance dashboard. Branch cards
// carry an up/down % comparison indicator for every total so managers
// can judge any window against any other at a glance.
//
// No engine files are imported or edited.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { isOwner } from "@/lib/AuthEngine";
import { todayBali } from "@/lib/ReportingEngine";
import { InfoHint, InfoHintInline } from "@/components/common/InfoHint";
import { CANCELLATION_TIPS } from "@/lib/reportTips";

// Iter-f7 — Default-range rule for every Overview dashboard:
//   Period A = first → last day of the PREVIOUS calendar month
//   Period B = first day of CURRENT month → today (Bali)
// Both ranges remain editable after mount; this is only the on-load default.
function previousMonthRange(todayISO) {
  const [y, m] = todayISO.split("-").map(Number);
  const prevYear  = m === 1 ? y - 1 : y;
  const prevMonth = m === 1 ? 12 : m - 1;
  const lastDay   = new Date(Date.UTC(prevYear, prevMonth, 0)).getUTCDate();
  const mm = String(prevMonth).padStart(2, "0");
  const dd = String(lastDay).padStart(2, "0");
  return [`${prevYear}-${mm}-01`, `${prevYear}-${mm}-${dd}`];
}
function currentMonthToToday(todayISO) {
  const [y, m] = todayISO.split("-");
  return [`${y}-${m}-01`, todayISO];
}

// Formatter mirrors Service Performance: "01 Oct 2026".
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function formatDate(d) {
  if (!d) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  if (!m) return d;
  return `${m[3]} ${MONTHS[parseInt(m[2], 10) - 1]} ${m[1]}`;
}

export default function BrandCancellationDashboard() {
  const { staff } = useAuth();
  const nav = useNavigate();
  const today = todayBali();
  const [aFrom, setAFrom] = useState(() => previousMonthRange(today)[0]);
  const [aTo,   setATo]   = useState(() => previousMonthRange(today)[1]);
  const [bFrom, setBFrom] = useState(() => currentMonthToToday(today)[0]);
  const [bTo,   setBTo]   = useState(() => currentMonthToToday(today)[1]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  // Iter-412 — Cancellation sub-menu access gated by the per-user
  // `cancellation` tick on Brand Report Permissions. Owner bypasses.
  const [brandCancellationTick, setBrandCancellationTick] = useState(false);
  useEffect(() => {
    let cancelled = false;
    staffApi.get("/report-permissions/me", { params: { scope: "brand" } })
      .then((r) => {
        if (cancelled) return;
        const keys = new Set(r.data?.allowed_keys || []);
        setBrandCancellationTick(keys.has("cancellation"));
      })
      .catch(() => { /* non-blocking — backend remains the authority */ });
    return () => { cancelled = true; };
  }, []);
  const canSeeCancellation = isOwner(staff) || brandCancellationTick;

  const params = useMemo(() => ({
    a_from: aFrom, a_to: aTo, b_from: bFrom, b_to: bTo,
  }), [aFrom, aTo, bFrom, bTo]);

  const load = useCallback(() => {
    if (!canSeeCancellation) return;
    setLoading(true);
    staffApi.get("/reports/brand/cancellations/summary", { params })
      .then((r) => setSummary(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [canSeeCancellation, params]);

  useEffect(() => { load(); }, [load]);

  const branches = summary?.branches || [];

  return (
    <StaffShell context="brand">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Cancellation</h1>
          <p
            className="text-xs font-medium text-sky-600 mt-1"
            data-testid="brand-cancellation-date-label"
          >
            Period A: {formatDate(aFrom)} → {formatDate(aTo)}
            &nbsp;vs&nbsp;
            Period B: {formatDate(bFrom)} → {formatDate(bTo)}
          </p>
        </div>
      </div>

      {!canSeeCancellation && (
        <Card className="rounded-lg mb-6">
          <CardContent className="p-8 text-sm text-muted-foreground text-center">
            You don't have permission to view the Cancellation dashboard.
          </CardContent>
        </Card>
      )}

      {canSeeCancellation && (
        <>
          <Card className="rounded-lg mb-4" data-testid="brand-cancellation-date-range">
            <CardContent className="p-3 flex items-center gap-3 flex-wrap">
              <RangePicker label="Period A" from={aFrom} to={aTo} onFrom={setAFrom} onTo={setATo} testIdPrefix="brand-cancellation-a" />
              <span className="h-6 w-px bg-border mx-1" />
              <RangePicker label="Period B" from={bFrom} to={bTo} onFrom={setBFrom} onTo={setBTo} testIdPrefix="brand-cancellation-b" />
              <Button
                size="sm"
                variant="outline"
                onClick={load}
                disabled={loading}
                className="gap-1 ml-auto"
                data-testid="brand-cancellation-refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </CardContent>
          </Card>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <TotalCard
              label="TOTAL CLIENT CANCELLATIONS"
              valueA={summary?.grand_client_cancellations_a}
              valueB={summary?.grand_client_cancellations_b}
              delta={summary?.grand_client_cancellations_delta_pct}
              tone="sky"
              caption="Unique client × date"
              loading={loading}
              testId="brand-cancellation-total-clients"
              tooltip={CANCELLATION_TIPS.clients}
            />
            <TotalCard
              label="TOTAL APPOINTMENT CANCELLATIONS"
              valueA={summary?.grand_appointment_cancellations_a}
              valueB={summary?.grand_appointment_cancellations_b}
              delta={summary?.grand_appointment_cancellations_delta_pct}
              tone="amber"
              caption="Singles + 1 per package"
              loading={loading}
              testId="brand-cancellation-total-appointments"
              tooltip={CANCELLATION_TIPS.appointments}
            />
            <TotalCard
              label="TOTAL PACKAGE CANCELLATIONS"
              valueA={summary?.grand_package_cancellations_a}
              valueB={summary?.grand_package_cancellations_b}
              delta={summary?.grand_package_cancellations_delta_pct}
              tone="rose"
              caption="1 per cancelled package"
              loading={loading}
              testId="brand-cancellation-total-packages"
              tooltip={CANCELLATION_TIPS.packages}
            />
          </div>

          <p className="tracking-overline text-[10px] text-muted-foreground mb-3">BY LOCATION</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {branches.map((b) => {
              const slug = (b.branch_name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <Card
                  key={b.branch_id}
                  onClick={() => nav(`/staff/brand/cancellations/${b.branch_id}?a_from=${aFrom}&a_to=${aTo}&b_from=${bFrom}&b_to=${bTo}`)}
                  className="rounded-lg cursor-pointer transition-shadow hover:shadow-md hover:ring-1 hover:ring-primary/30"
                  data-testid={`brand-cancellation-branch-${slug}`}
                >
                  <CardContent className="p-5">
                    <p className="font-medium">{b.branch_name}</p>
                    <div className="grid grid-cols-3 gap-3 mt-4">
                      <MetricCell
                        label="Clients"
                        valueA={b.client_cancellations_a}
                        valueB={b.client_cancellations_b}
                        delta={b.client_cancellations_delta_pct}
                        tone="sky"
                        testId={`brand-cancellation-branch-${slug}-clients`}
                        tooltip={CANCELLATION_TIPS.clients}
                      />
                      <MetricCell
                        label="Appts"
                        valueA={b.appointment_cancellations_a}
                        valueB={b.appointment_cancellations_b}
                        delta={b.appointment_cancellations_delta_pct}
                        tone="amber"
                        testId={`brand-cancellation-branch-${slug}-appointments`}
                        tooltip={CANCELLATION_TIPS.appointments}
                      />
                      <MetricCell
                        label="Packages"
                        valueA={b.package_cancellations_a}
                        valueB={b.package_cancellations_b}
                        delta={b.package_cancellations_delta_pct}
                        tone="rose"
                        testId={`brand-cancellation-branch-${slug}-packages`}
                        tooltip={CANCELLATION_TIPS.packages}
                      />
                    </div>
                    <p className="text-[10px] uppercase tracking-wider text-primary/80 mt-4">
                      Open branch details →
                    </p>
                  </CardContent>
                </Card>
              );
            })}
            {branches.length === 0 && !loading && (
              <Card className="rounded-lg sm:col-span-2 lg:col-span-3">
                <CardContent className="p-8 text-sm text-muted-foreground text-center">
                  No cancellation records in the selected ranges.
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </StaffShell>
  );
}

function RangePicker({ label, from, to, onFrom, onTo, testIdPrefix }) {
  return (
    <div className="flex items-center gap-2">
      <span className="tracking-overline text-[10px] text-muted-foreground">{label}</span>
      <Input
        type="date"
        value={from}
        onChange={(e) => onFrom(e.target.value)}
        className="h-8 w-40 text-xs"
        data-testid={`${testIdPrefix}-from`}
      />
      <span className="text-xs text-muted-foreground">to</span>
      <Input
        type="date"
        value={to}
        onChange={(e) => onTo(e.target.value)}
        className="h-8 w-40 text-xs"
        data-testid={`${testIdPrefix}-to`}
      />
    </div>
  );
}

function TotalCard({ label, valueA, valueB, delta, tone, caption, loading, testId, tooltip }) {
  const toneColor = {
    sky: "text-sky-600",
    amber: "text-amber-600",
    rose: "text-rose-600",
  }[tone] || "text-foreground";
  return (
    <Card className="rounded-lg relative" data-testid={testId}>
      <InfoHint text={tooltip} testId={`${testId}-info`} />
      <CardContent className="p-5">
        <p className="tracking-overline text-[10px] text-muted-foreground pr-5">{label}</p>
        <p className={`text-3xl font-semibold mt-2 ${toneColor}`}>
          {loading ? "…" : (valueA ?? 0).toLocaleString("en-US")}
        </p>
        <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px]">
          <DeltaBadge delta={delta} testId={`${testId}-delta`} />
          <span className="text-muted-foreground" data-testid={`${testId}-b`}>
            B · {(valueB ?? 0).toLocaleString("en-US")}
          </span>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">{caption}</p>
      </CardContent>
    </Card>
  );
}

function MetricCell({ label, valueA, valueB, delta, tone, testId, tooltip }) {
  const toneColor = {
    sky: "text-sky-600",
    amber: "text-amber-600",
    rose: "text-rose-600",
  }[tone] || "text-foreground";
  return (
    <div data-testid={testId}>
      <p className="text-[11px] text-muted-foreground flex items-center gap-1">
        {label}
        <InfoHintInline text={tooltip} testId={`${testId}-info`} />
      </p>
      <p className={`text-xl font-semibold mt-0.5 tabular-nums ${toneColor}`}>
        {(valueA ?? 0).toLocaleString("en-US")}
      </p>
      <DeltaBadge delta={delta} testId={`${testId}-delta`} />
      <p className="text-[10px] text-muted-foreground mt-0.5">
        B · {(valueB ?? 0).toLocaleString("en-US")}
      </p>
    </div>
  );
}

function DeltaBadge({ delta, testId }) {
  let Icon = Minus;
  let cls = "text-muted-foreground";
  if (delta != null && delta > 0) { Icon = ArrowUpRight; cls = "text-emerald-600"; }
  if (delta != null && delta < 0) { Icon = ArrowDownRight; cls = "text-rose-600"; }
  const label = delta == null ? "—" : `${delta > 0 ? "+" : ""}${delta}%`;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${cls}`}
      data-testid={testId}
    >
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}
